import React from 'react';

import { Flag, Search, X } from 'lucide-react';

import PlayedPill from './PlayedPill';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';
import ClosedBadge from '../common/ClosedBadge';

interface ExploreSearchBarProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  results: CourseMinifiedDTO[];
  onResultClick: (course: CourseMinifiedDTO) => void;
  isLoading: boolean;
  totalCourses: number;
  // The played state, shown and set on the result row itself. Optional as a pair: leave both out
  // and the rows render with no pill, which is what a surface that cannot set the state wants.
  isPlayed?: (course: CourseMinifiedDTO) => boolean;
  onTogglePlayed?: (course: CourseMinifiedDTO) => void;
  /**
   * Whether a real search is running, so an empty `results` means "nothing found" rather than
   * "nothing asked". Passed rather than derived from searchTerm, because the term this component
   * shows is the one being typed and the results come from a debounced copy of it.
   */
  isSearching?: boolean;
  /**
   * What the courses are currently narrowed to, e.g. "South Africa". Named in the empty state so
   * a search that finds nothing says why, instead of reading as though the club is missing. Null
   * when nothing is filtered.
   */
  filterScope?: string | null;
  /**
   * Asking for a course that is not on GolfPlayed. Offered under the results and in the empty
   * state. Optional: leave it out and neither shows.
   */
  onRequestCourse?: () => void;
  /**
   * A control drawn inside the box at its right-hand end. Explore puts the Map / List switch here
   * (the client's call, 2026-11-04), so the search and the view sit in one bubble.
   */
  trailing?: React.ReactNode;
}

// The one "not here?" control, used in both places it appears so the two cannot drift apart.
const RequestCourseButton: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
  >
    <Flag className="h-4 w-4 shrink-0" />
    <span>
      Can&apos;t find it?{' '}
      <span className="font-semibold">Request a course</span>
    </span>
  </button>
);

const ExploreSearchBar: React.FC<ExploreSearchBarProps> = ({
  searchTerm,
  onSearchTermChange,
  results,
  onResultClick,
  isLoading,
  totalCourses,
  isPlayed,
  onTogglePlayed,
  isSearching = false,
  filterScope = null,
  onRequestCourse,
  trailing,
}) => (
  // Positioning lives on the top bar in Explore.tsx, which this shares with the view toggle, so
  // the two cannot drift out of alignment. Growing to fill that centred row is what makes the box
  // wider than it was — the width is not set here.
  <div className="relative min-w-0 flex-1">
    {/* The bubble is the wrapper, not the input, so a control can sit inside it at the right
        (see `trailing`). The input is borderless and fills what is left. focus-within puts the
        gold focus ring on the whole bubble, which is what the visitor sees as the box. */}
    <div className="flex h-12 w-full items-center rounded-full border border-primary/30 bg-charcoal/90 pr-1 shadow-2xl backdrop-blur transition-all focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
      <Search className="pointer-events-none ml-4 h-4 w-4 shrink-0 text-primary/70" />
      <input
        type="search"
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        placeholder={
          isLoading
            ? `Loading courses...`
            : `Search ${totalCourses.toLocaleString()} courses...`
        }
        disabled={isLoading}
        aria-label="Search the courses"
        // The native clear button is switched off. `type="search"` gets one for free in Chrome
        // and desktop Safari, but not in iOS Safari, which is why the box had a clear on a
        // desktop and none on a phone: the one that was there was never ours. It is also styled
        // by the browser, so it came out blue on a bar that is charcoal and gold. Ours below is
        // the same control at every width, in the product's own colours.
        className="h-full min-w-0 flex-1 bg-transparent pl-3 pr-1 text-sm text-white outline-none placeholder:text-white/40 disabled:cursor-wait disabled:opacity-70 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
      />

      {/* Only once there is something to clear. An X sitting on an empty box is a control that
          does nothing, and on a phone it is a control that does nothing right where the thumb
          lands. White, going gold on press, the same pair every other close in this product
          uses. h-9 w-9 keeps the tap target at the 36px a thumb needs while the mark itself
          stays small. */}
      {searchTerm.length > 0 && (
        <button
          type="button"
          onClick={() => onSearchTermChange('')}
          aria-label="Clear the search"
          title="Clear the search"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {trailing}
    </div>

    {/* Floated out of flow on purpose. In normal flow the results would stretch the blurred bar
        to fit them, so typing would balloon a 72px bar into a 380px one.

        Hangs off the search box and matches its width, at every size. It used to be `fixed` below
        sm, pinned 4.75rem down the page: back then the box shared the phone's bar with the view
        toggle and the account menu, so matching its width left a panel about 240px wide with
        nowhere to put a club name, and breaking out to the full page was the fix. The box has its
        own full-width row on a phone now, so there is nothing left to escape, and that measured
        offset had become wrong anyway: the box moved down out of the bar and the results landed
        on top of it. */}
    {results.length > 0 && (
      <ul className="absolute inset-x-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-xl border border-primary/20 bg-charcoal/95 shadow-2xl backdrop-blur sm:mt-4">
        {results.map((course, index) => {
          const showPill = isPlayed != null && onTogglePlayed != null;

          return (
            <li
              key={course.id}
              className={`flex items-center gap-2 pr-3 transition-colors hover:bg-primary/10 ${
                index !== 0 ? 'border-t border-white/5' : ''
              }`}
            >
              {/* The row and the pill are two separate targets, so the row can no longer be one
                  <button> wrapping everything: a button may not contain another button. Same
                  split CourseCompactCard makes, for the same reason. */}
              <button
                type="button"
                onClick={() => onResultClick(course)}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left text-sm"
              >
                {/* Square thumbnail spanning both text rows. This is the same `logoUrl` the
                    detail panel and the course card read — it ships with the course data, so
                    a row costs no extra request. A course with no logo of its own gets the
                    GolfPlayed mark rather than collapsing, so every row is the same height and
                    the names stay in one column.
                    White plate and object-contain, matching the card and the panel: these are
                    wordmarks on their own backgrounds, and object-cover cropped them. */}
                <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-0.5 ring-1 ring-black/20">
                  <img
                    src={courseLogo(course).src}
                    alt=""
                    className="h-full w-full object-contain"
                    draggable={false}
                  />
                </span>

                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-white">
                    {course.name}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-xs text-primary/70">
                      {[course.stateName, course.countryName]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                    <ClosedBadge
                      closed={course.closed}
                      closedYear={course.closedYear}
                      closedMonth={course.closedMonth}
                    />
                  </span>
                </span>
              </button>

              {/* Searching for a club and ticking it played is the shortest path through the app,
                  and until now it took three steps: search, pick, tick in the drawer. The pill
                  makes it one. It does not close the list, so a member can work down a run of
                  results without retyping. */}
              {showPill && (
                <PlayedPill
                  courseName={course.name ?? ''}
                  isPlayed={isPlayed(course)}
                  onToggle={() => onTogglePlayed(course)}
                />
              )}
            </li>
          );
        })}

        {/* The search is fuzzy, so a club that is not here usually still brings back a few
            near-misses rather than nothing. Without this row the only way to ask for it would be
            to keep typing until the list came back empty. */}
        {onRequestCourse != null && (
          <li className="border-t border-white/10">
            <RequestCourseButton onClick={onRequestCourse} />
          </li>
        )}
      </ul>
    )}

    {/* Nothing found. Same panel as the results so the answer arrives where the answer was
        expected, rather than the list simply never appearing.
        The search runs over the filtered set on purpose, so a club that really exists can come
        back empty. Naming the filter is what stops that reading as "this club is not in
        GolfPlayed". With nothing filtered there is nothing to blame and the plain line is the
        whole truth. */}
    {isSearching && results.length === 0 && (
      <div className="absolute inset-x-0 top-full z-50 mt-2 rounded-xl border border-primary/20 bg-charcoal/95 px-4 py-4 shadow-2xl backdrop-blur sm:mt-4">
        <p className="text-sm text-white/80">
          {filterScope != null
            ? `No matches in ${filterScope}.`
            : 'No courses match that.'}
        </p>
        {filterScope != null && (
          <p className="mt-1 text-xs text-white/40">
            Clear the filter to search every course.
          </p>
        )}
        {onRequestCourse != null && (
          <div className="-mx-4 -mb-4 mt-3 overflow-hidden rounded-b-xl border-t border-white/10">
            <RequestCourseButton onClick={onRequestCourse} />
          </div>
        )}
      </div>
    )}
  </div>
);

export default ExploreSearchBar;
